// =============================================================================
// token-service.ts — pure-function port of Unity TokenService.cs
// No class, no singleton, no module-level state.
// =============================================================================

import type { ActionLog } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// ---------------------------------------------------------------------------
// PlayerTokenState  (M: frozen plain object instead of mutable Map)
// ---------------------------------------------------------------------------

export interface PlayerTokenState {
  /** seat index → token balance. Missing key treated as 0. */
  readonly balances: Readonly<Record<string, number>>;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export interface TrySpendResult {
  readonly ok: boolean;
  readonly newState: PlayerTokenState;
  readonly log: ActionLog;
  /** Balance before the spend attempt. */
  readonly prev: number;
  /** Balance after spend (equals prev when ok=false). (H: exact-zero bankruptcy signal) */
  readonly next: number;
}

export interface AddResult {
  readonly newState: PlayerTokenState;
  readonly log: ActionLog;
  /**
   * True when the balance was clamped to 0 (went negative).
   * Caller is responsible for triggering bankruptcy logic when this is true.
   */
  readonly clampedToZero: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBalance(state: PlayerTokenState, seat: number): number {
  return state.balances[seat] ?? 0;
}

function setBalance(state: PlayerTokenState, seat: number, value: number): PlayerTokenState {
  const next = Object.freeze({ ...state.balances, [seat]: value });
  return Object.freeze({ balances: next });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the current token balance for a seat (0 if not set).
 */
export function getTokenBalance(state: PlayerTokenState, seat: number): number {
  return getBalance(state, seat);
}

/**
 * Attempt to spend `amount` tokens from `seat`.
 * Returns ok=false (no state change) on shortfall or negative amount.
 * Negative amounts are rejected and logged as errors via console.error.
 * prev/next always populated — callers check next===0 to orchestrate bankruptcy. (H)
 */
export function trySpend(
  state: PlayerTokenState,
  log: ActionLog,
  seat: number,
  amount: number,
  reason: string,
): TrySpendResult {
  // L: input validation
  assertFiniteInt(seat, "trySpend seat");
  assertFiniteInt(amount, "trySpend amount");

  if (amount < 0) {
    console.error(
      `[TokenService] TrySpend negative amount ${amount} from seat ${seat} source=${reason}`,
    );
    const prev = getBalance(state, seat);
    return { ok: false, newState: state, log, prev, next: prev };
  }
  const prev = getBalance(state, seat);
  if (prev < amount) {
    const nextLog = log.append({
      kind: "token.spend.insufficient",
      data: { seat, amount, reason, balanceBefore: prev },
    });
    return { ok: false, newState: state, log: nextLog, prev, next: prev };
  }
  const next = prev - amount;
  const newState = setBalance(state, seat, next);
  const nextLog = log.append({
    kind: "token.spend",
    data: { seat, amount, reason, balanceBefore: prev, balanceAfter: next },
  });
  return {
    ok: true,
    newState: Object.freeze(newState),
    log: nextLog,
    prev,
    next,
  };
}

/**
 * Add `delta` tokens to `seat`. Clamps to 0 on underflow.
 * When `clampedToZero` is true on the result, the caller must trigger
 * bankruptcy (pure: this function does not reach into BankruptcyService).
 */
export function addTokens(
  state: PlayerTokenState,
  log: ActionLog,
  seat: number,
  delta: number,
  source: string,
): AddResult {
  // L: input validation
  assertFiniteInt(seat, "addTokens seat");
  assertFiniteInt(delta, "addTokens delta");

  const prev = getBalance(state, seat);
  const raw = prev + delta;
  const clampedToZero = raw <= 0;
  const next = clampedToZero ? 0 : raw;
  const newState = setBalance(state, seat, next);
  const nextLog = log.append({
    kind: "token.add",
    data: {
      seat,
      delta,
      source,
      balanceBefore: prev,
      balanceAfter: next,
      clamped: clampedToZero,
    },
  });
  return {
    newState: Object.freeze(newState),
    log: nextLog,
    clampedToZero,
  };
}

/**
 * Create initial empty token state.
 */
export function createTokenState(): PlayerTokenState {
  return Object.freeze({ balances: Object.freeze({}) });
}

/**
 * Set a seat balance directly (for test setup / game init).
 */
export function setTokenBalance(
  state: PlayerTokenState,
  seat: number,
  value: number,
): PlayerTokenState {
  // L: input validation
  assertFiniteInt(seat, "setTokenBalance seat");
  assertFiniteInt(value, "setTokenBalance value");
  return Object.freeze(setBalance(state, seat, value));
}
